import { inject } from "@angular/core";
import {
  CanActivateFn,
  Router,
  UrlTree,
} from "@angular/router";

import { ErrorPageCode } from "../../app.enums";
import { ApplicationFactory } from "../../factories/ApplicationFactory";

import {
  GenericErrorHandler,
  ActivityIndicatorService,
  UserRoleEnum,
  ApplicationTypeEnum,
  IUserDetails,
  CLAIM_NAME,
  CLAIM_PICTURE,
  CLAIM_EMAIL,
} from "../../../core-ui";
import { UserService } from "../../../core-ui/services/user.service";
import { AuthenticationService } from "../../../core-ui/services/authentication.service";
import { ApplicationService } from "../../../core-ui/services/application.service";
import { LocaleService } from "../../services";

export const authAgentGuard: CanActivateFn = async (
  route,
  state,
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const errorHandler = inject(GenericErrorHandler);
  const activity = inject(ActivityIndicatorService);
  const authenticationService = inject(AuthenticationService);
  const userService = inject(UserService);
  const applicationService = inject(ApplicationService);
  const applicationFactory = inject(ApplicationFactory);
  const locale = inject(LocaleService);

  if (!userService.isAuthenticated()) {
    try {
      // no message: when the sign-in popup is blocked or interrupted the strategy replaces this
      // spinner with a card that says so, and warning about popups up front only confused agents
      // whose popup did open.
      activity.loading(true, "", true);

      const user = await authenticationService.authenticate(route);

      if (!user.hasRole(UserRoleEnum.agent)) {
        // TODO: this message is displayed on the ui
        throw new Error("user is not an agent");
      }

      userService.setActiveUser(user);

      // refresh application now that we have a jwt token
      applicationService.createApiResource();
      const application =
        await applicationFactory.createAgentApplication(
          applicationService.getActiveApplication().getId(),
          route
        );
      await applicationService.setActiveApplication(application);

      // todo: do it better. on genesys it is set on GenesysCloudApplication / afterAuth.
      if (
        [
          ApplicationTypeEnum.StandaloneOpenID,
          ApplicationTypeEnum.TalkdeskOpenID,
          ApplicationTypeEnum.NiceOpenID,
        ].includes(application.getType())
      ) {
        const details: IUserDetails = {
          id: user.getId(),
          name: user.getClaim(CLAIM_NAME),
          avatarUrl: user.getClaim(CLAIM_PICTURE),
          email: user.getClaim(CLAIM_EMAIL),
        };
        userService.setUserDetails(details);
      }
    } catch (error) {
      errorHandler.handleError(error);
      let message = error.message;
      if (error.status === 401) {
        message = "Please reload the page to try again. " + error.message;
      }
      return router.createUrlTree(
        ["/error", ErrorPageCode.AUTHENTICATION_FAILURE],
        {
          queryParams: { message },
          queryParamsHandling: "merge",
        }
      );
    } finally {
      activity.loading(false);
    }
  }
   // try fix for race condition where sometimes the locale.guard was not authenticated
  // await loadCustomTranslations();
  return true;
};
